import { Crown, UserCheck, HeartHandshake, MessagesSquare, ShieldCheck, ChefHat, Croissant, Martini, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import PageHero, { NextPage } from "@/components/PageHero";
import { FadeUp, StaggerGroup, StaggerItem } from "@/components/Reveal";

const COUNTIES = ["Buckinghamshire", "London", "Hertfordshire", "Oxfordshire", "Essex", "Berkshire"];

const FOCUS = [
  {
    title: "Self-worth",
    copy: "Building every boy's sense that he is valued, capable and worth investing in.",
    Icon: Crown,
    color: "#FFB019",
  },
  {
    title: "Positive role modelling",
    copy: "Consistent adults who show what respect, responsibility and character look like.",
    Icon: UserCheck,
    color: "#FF508D",
  },
  {
    title: "Healthy relationships",
    copy: "Learning trust, boundaries and respect in friendships, family and community.",
    Icon: HeartHandshake,
    color: "#5CFFAF",
  },
  {
    title: "Emotional literacy",
    copy: "Finding the words for feelings, and handling them with confidence.",
    Icon: MessagesSquare,
    color: "#00A5FF",
  },
  {
    title: "Safe, life-affirming choices",
    copy: "Making decisions that keep him safe and move his future forward.",
    Icon: ShieldCheck,
    color: "#696DFF",
  },
];

const COOKING = [
  { title: "Baking together", Icon: Croissant, color: "#FF9137" },
  { title: "Creating delicious mocktails", Icon: Martini, color: "#FF508D" },
  { title: "Conversations whilst we create", Icon: MessagesSquare, color: "#00A5FF" },
];

export default function YoungKings() {
  return (
    <div data-testid="page-young-kings">
      <PageHero
        testid="youngkings-hero"
        eyebrow="Young Kings Programme"
        title={[
          "Growing confident,",
          <>
            respectful, resilient <em className="text-rainbow italic">young men</em>
          </>,
        ]}
        lead="Our Young Kings Program is a unique, empowering initiative designed to support teenage boys aged 12-15 in growing into confident, respectful, and resilient young men."
      />

      {/* Where it's delivered */}
      <section className="container-x" data-testid="delivery-section">
        <FadeUp>
          <div className="card flex flex-wrap items-center gap-3 p-7 sm:p-8" data-testid="delivery-card">
            <p className="w-full text-xs font-extrabold uppercase tracking-[0.18em] text-mute sm:w-auto sm:mr-2">
              Delivered in our home, schools and communities across
            </p>
            {COUNTIES.map((county, i) => (
              <span key={county} className="chip bg-sand !normal-case !tracking-normal !text-sm !font-bold !px-5 !py-2.5" data-testid={`yk-county-${i + 1}`}>
                <span className="dot" style={{ background: ["#FF508D", "#FF9137", "#FFB019", "#5CFFAF", "#00A5FF", "#696DFF"][i] }} />
                {county}
              </span>
            ))}
          </div>
        </FadeUp>
      </section>

      {/* Full introduction */}
      <section className="container-x pt-16 sm:pt-20" data-testid="intro-section">
        <FadeUp>
          <p className="max-w-3xl text-lg leading-relaxed text-ink/90 sm:text-xl" data-testid="intro-paragraph">
            Our Young Kings Program is a unique, empowering initiative designed to support teenage boys aged
            12-15 in growing into confident, respectful, and resilient young men. Delivered both within our
            home and out in schools and local communities across Buckinghamshire, London, Hertfordshire,
            Oxfordshire, Essex, and Berkshire, the program focuses on building self-worth, positive role
            modelling, healthy relationships, emotional literacy, and making safe, life-affirming choices —
            all brought to life through cooking therapy, where we explore conversations whilst baking and
            creating delicious mocktails together. It's a relaxed, meaningful way to connect, learn, and grow
            — teaching the boys that they are valued, capable, and the masters of their own future.
          </p>
        </FadeUp>
      </section>

      {/* Focus areas */}
      <section className="container-x py-16 sm:py-20 lg:py-24" data-testid="focus-section">
        <FadeUp>
          <p className="eyebrow">What the programme builds</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl tracking-tight text-ink sm:text-4xl" data-testid="focus-title">
            Five foundations for growing up strong
          </h2>
        </FadeUp>
        <StaggerGroup className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FOCUS.map(({ title, copy, Icon, color }) => (
            <StaggerItem key={title}>
              <div className="card h-full p-8 transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_24px_45px_-28px_rgba(45,55,72,0.45)]" data-testid={`focus-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sand text-ink">
                    <Icon className="h-6 w-6" strokeWidth={1.5} />
                  </span>
                  <span className="dot" style={{ background: color }} aria-hidden="true" />
                </div>
                <h3 className="mt-6 font-display text-2xl text-ink">{title}</h3>
                <p className="mt-3 text-base leading-relaxed text-mute">{copy}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>

      {/* Cooking therapy */}
      <section className="container-x" data-testid="cooking-section">
        <FadeUp>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-ink px-7 py-14 sm:px-14 sm:py-20">
            <div aria-hidden="true" className="absolute inset-x-0 top-0 h-1.5 bg-rainbow" />
            <p className="eyebrow !text-white/60">Brought to life through cooking therapy</p>
            <h2 className="mt-5 max-w-2xl font-display text-3xl tracking-tight text-white sm:text-4xl" data-testid="cooking-title">
              Conversations whilst baking, and delicious mocktails together
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70" data-testid="cooking-copy">
              It's a relaxed, meaningful way to connect, learn, and grow — the kitchen is where the
              programme's biggest conversations happen.
            </p>
            <div className="mt-10 grid gap-5 sm:grid-cols-3">
              {COOKING.map(({ title, Icon, color }) => (
                <div key={title} className="rounded-3xl border border-white/15 p-6" data-testid={`cooking-${title.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 20)}`}>
                  <Icon className="h-6 w-6 text-sun" strokeWidth={1.5} />
                  <p className="mt-4 text-base font-bold text-white">{title}</p>
                </div>
              ))}
            </div>
          </div>
        </FadeUp>
      </section>

      {/* Message */}
      <section className="container-x py-16 sm:py-20 lg:py-24" data-testid="message-section">
        <FadeUp>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-sand px-7 py-14 text-center sm:px-14 sm:py-20">
            <Crown className="mx-auto h-10 w-10 text-ink" strokeWidth={1.5} />
            <p className="mx-auto mt-6 max-w-3xl font-display text-2xl leading-snug text-ink sm:text-3xl lg:text-4xl" data-testid="message-statement">
              Teaching the boys that they are <em className="text-rainbow italic">valued, capable,</em> and
              the masters of their own future.
            </p>
          </div>
        </FadeUp>
      </section>

      {/* CTA */}
      <section className="container-x pb-4" data-testid="yk-cta">
        <FadeUp>
          <div className="flex flex-col items-start gap-6 rounded-3xl bg-sand px-7 py-8 sm:flex-row sm:items-center sm:justify-between sm:px-10">
            <p className="max-w-xl text-base font-bold text-ink" data-testid="yk-cta-copy">
              Want the Young Kings Programme in your school or community? Get in touch with the team.
            </p>
            <Link to="/referrals" data-testid="yk-cta-link" className="btn btn-primary !px-5 !py-2.5 text-[13px]">
              Referrals &amp; enquiries
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </div>
        </FadeUp>
      </section>

      <div className="container-x pb-4 pt-10">
        <NextPage
          to="/referrals"
          title="Referral, matching and contact"
          label="The five-step process and how to reach us"
          testid="next-referrals-from-youngkings"
        />
      </div>
    </div>
  );
}
